import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { toast } from "sonner";
import { Loader2, RefreshCw, Users, Search, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const STATUS_META = {
  trial: { label: "Free trial", cls: "bg-primary/15 text-primary", color: "#f97316" },
  active: { label: "Subscribed", cls: "bg-green-500/15 text-green-400", color: "#22c55e" },
  cancelling: { label: "Cancelling", cls: "bg-amber-500/15 text-amber-400", color: "#f59e0b" },
  trial_ended: { label: "Trial ended", cls: "bg-red-500/15 text-red-400", color: "#ef4444" },
  inactive: { label: "Inactive", cls: "bg-white/10 text-muted-foreground", color: "#6b7280" },
  blocked: { label: "Trial blocked", cls: "bg-red-500/15 text-red-400", color: "#ef4444" },
};

const PLAN_META = {
  pro: { label: "Pro", cls: "bg-primary/15 text-primary" },
  lite: { label: "Lite", cls: "bg-sky-500/15 text-sky-400" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

export default function Members() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/owner/signups"); setData(res.data); }
    catch (e) {
      if (e?.response?.status === 403) { navigate("/dashboard"); return; }
      toast.error(e?.response?.data?.detail || "Could not load sign-ups");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doDelete = async () => {
    if (!delTarget) return;
    setDelBusy(true);
    try {
      await api.delete(`/owner/signups/${delTarget.company_id}`);
      toast.success(`${delTarget.company_name || "Business"} permanently deleted.`);
      setDelTarget(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    setDelBusy(false);
  };

  const counts = data?.counts || { total: 0, trial: 0, active: 0, cancelling: 0, inactive: 0 };
  const items = (data?.items || []).filter((i) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [i.company_name, i.email, i.phone, i.location].some((v) => (v || "").toLowerCase().includes(s));
  });

  const chartData = [
    { name: "Subscribed", value: counts.active, key: "active" },
    { name: "Free trial", value: counts.trial, key: "trial" },
    { name: "Cancelling", value: counts.cancelling, key: "cancelling" },
    { name: "Inactive", value: counts.inactive, key: "inactive" },
  ].filter((d) => d.value > 0);

  const stats = [
    { label: "Total sign-ups", value: counts.total, cls: "text-foreground" },
    { label: "Subscribed", value: counts.active, cls: "text-green-400" },
    { label: "Pro subs", value: counts.pro, cls: "text-primary" },
    { label: "Lite subs", value: counts.lite, cls: "text-sky-400" },
    { label: "Yearly subs", value: counts.yearly, cls: "text-green-400" },
    { label: "Free trial", value: counts.trial, cls: "text-primary" },
    { label: "Cancelling", value: counts.cancelling, cls: "text-amber-400" },
    { label: "Not subscribed", value: counts.inactive, cls: "text-muted-foreground" },
  ];

  return (
    <div className="space-y-6" data-testid="members-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight flex items-center gap-3"><Users className="h-8 w-8 text-primary" /> SIGN-UPS</h1>
          <p className="text-muted-foreground mt-1">Every business on TradieDesk and their current plan. Owner-only.</p>
        </div>
        <Button data-testid="members-refresh" onClick={load} variant="outline" className="rounded-sm border-white/10 gap-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-[#18181b] border border-white/10 rounded-sm p-4" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
            <p className={`font-display text-3xl tracking-tight mt-1 ${s.cls}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Chart */}
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-5">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Plan breakdown</p>
          {chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">No sign-ups yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                  {chartData.map((d) => <Cell key={d.key} fill={STATUS_META[d.key]?.color || "#6b7280"} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, color: "#fff" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Table */}
        <div className="lg:col-span-2 bg-[#18181b] border border-white/10 rounded-sm p-5">
          <div className="relative mb-4">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="members-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone or location…" className="rounded-sm bg-[#0f0f11] border-white/10 pl-9" />
          </div>
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center" data-testid="members-empty">No businesses match.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="members-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10">
                    <th className="py-2 pr-4 font-medium">Company</th>
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Phone</th>
                    <th className="py-2 pr-4 font-medium">Location</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Plan</th>
                    <th className="py-2 pr-2 font-medium">Joined</th>
                    <th className="py-2 pr-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i, idx) => {
                    const meta = STATUS_META[i.status] || STATUS_META.inactive;
                    return (
                      <tr key={idx} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`member-row-${idx}`}>
                        <td className="py-2.5 pr-4 font-medium">{i.company_name || "—"}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{i.email || "—"}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{i.phone || "—"}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{i.location || "—"}</td>
                        <td className="py-2.5 pr-4">
                          <span className={`text-xs uppercase tracking-wide px-2 py-1 rounded-sm font-semibold ${meta.cls}`}>{meta.label}</span>
                          {i.status === "cancelling" && i.current_period_end && (
                            <span className="block text-[11px] text-muted-foreground mt-0.5">ends {fmtDate(i.current_period_end)}</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4">
                          {i.plan && (i.status === "active" || i.status === "cancelling") ? (
                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                              <span className={`text-xs uppercase tracking-wide px-2 py-0.5 rounded-sm font-semibold ${(PLAN_META[i.plan] || PLAN_META.pro).cls}`}>{(PLAN_META[i.plan] || PLAN_META.pro).label}</span>
                              <span className="text-[11px] text-muted-foreground">{i.billing_interval === "year" ? "Yearly" : "Monthly"}</span>
                            </div>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="py-2.5 pr-2 text-muted-foreground whitespace-nowrap">{fmtDate(i.created_at)}</td>
                        <td className="py-2.5 pr-2 text-right">
                          <Button data-testid={`member-delete-${idx}`} size="icon" variant="ghost" onClick={() => { setConfirmText(""); setDelTarget(i); }} className="h-8 w-8 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10" title="Delete business">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <DialogContent className="bg-[#18181b] border-white/10 text-foreground" data-testid="member-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight text-red-400">Delete this business?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This permanently deletes <span className="text-foreground font-semibold">{delTarget?.company_name}</span> ({delTarget?.email}) — their account, all leads, bookings and settings — and cancels their subscription immediately. This <span className="text-red-400 font-semibold">cannot be undone</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <p className="text-sm text-muted-foreground mb-2">Type <span className="font-mono font-semibold text-foreground">DELETE</span> to confirm.</p>
            <Input data-testid="member-delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="rounded-sm bg-[#0f0f11] border-white/10" />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button data-testid="member-delete-cancel" variant="outline" onClick={() => setDelTarget(null)} className="rounded-sm border-white/10">Keep business</Button>
            <Button data-testid="member-delete-confirm" onClick={doDelete} disabled={delBusy || confirmText !== "DELETE"} className="rounded-sm bg-red-500 hover:bg-red-600 text-white font-semibold gap-2 disabled:opacity-50">
              {delBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Permanently delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
